import { makeObservable, computed } from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import {
  SpaceAccountNoteAttachmentModelData,
  NoteAttachmentProcessingStatus,
  NoteAttachmentHostingStatus,
  NoteAttachmentHostingFailureReason,
  ProcessingInfo,
} from "@/store/note-attachments/types";

export class SpaceAccountNoteAttachmentObservable extends BaseSyncModel<SpaceAccountNoteAttachmentModelData> {
  public modelKind = SyncModelKind.SpaceAccountNoteAttachment;

  constructor({
    id,
    data,
    store,
  }: { id: string; data: SyncUpdateValue<SpaceAccountNoteAttachmentModelData> } & WithAppStore) {
    super({ id, data, store });

    makeObservable(this, {
      modelKind: false,
      associatedNoteAttachmentId: computed,
      associatedNoteId: computed,
      attachmentUrl: computed,
      servingUrl: computed,
      hostingStatus: computed,
      hostingFailureReason: computed,
      mimeType: computed,
      filename: computed,
      pageCount: computed,
      sizeBytes: computed,
      extractionStatus: computed,
      indexingStatus: computed,
      processingInfo: computed,
      suggestedSearch: computed,
      ocrMarkdown: computed,
      isProcessingComplete: computed,
      isProcessingFailed: computed,
      isProcessing: computed,
    });
  }

  get associatedNoteAttachmentId(): string {
    return this.modelData.associated_note_attachment_id;
  }

  get associatedNoteId(): string {
    return this.modelData.associated_note_id;
  }

  get attachmentUrl(): string | null {
    return this.modelData.attachment_url;
  }

  get servingUrl(): string | null {
    return this.modelData.serving_url;
  }

  get hostingStatus(): NoteAttachmentHostingStatus | null {
    return this.modelData.hosting_status;
  }

  get hostingFailureReason(): NoteAttachmentHostingFailureReason | null {
    return this.modelData.hosting_failure_reason;
  }

  get mimeType(): string | null {
    return this.modelData.mime_type;
  }

  get filename(): string | null {
    return this.modelData.filename;
  }

  get pageCount(): number | null {
    return this.modelData.page_count;
  }

  get sizeBytes(): number | null {
    return this.modelData.size_bytes;
  }

  get extractionStatus(): NoteAttachmentProcessingStatus | null {
    return this.modelData.extraction_status;
  }

  get indexingStatus(): NoteAttachmentProcessingStatus | null {
    return this.modelData.indexing_status;
  }

  get processingInfo(): ProcessingInfo | null {
    return this.modelData.processing_info;
  }

  get suggestedSearch(): string | null {
    return this.modelData.suggested_search;
  }

  get ocrMarkdown(): string | null {
    return this.modelData.ocr_markdown;
  }

  get isProcessingComplete(): boolean {
    return this.extractionStatus === "COMPLETED" && this.indexingStatus === "COMPLETED";
  }

  get isProcessingFailed(): boolean {
    return this.extractionStatus === "FAILED" || this.indexingStatus === "FAILED";
  }

  get isProcessing(): boolean {
    const activeStatuses: NoteAttachmentProcessingStatus[] = ["PENDING", "IN_PROGRESS"];
    return (
      (this.extractionStatus !== null && activeStatuses.includes(this.extractionStatus)) ||
      (this.indexingStatus !== null && activeStatuses.includes(this.indexingStatus))
    );
  }
}
